import React, { useState } from 'react'
import {
	Button, Card, Input, Modal, message
} from 'antd'
import { EditOutlined } from '@ant-design/icons'
import {
	TELEPHONE_SURVEY_CALL_STATUS,
	TELEPHONE_SURVEY_SATISFACTION
} from '../../../reducers/telephoneSurveys/actions'
import { patchReq } from '../../../utils/request'

const formatPhone = (phone: string | null | undefined): string => {
	if (!phone) return '-'
	const digits = phone.replace(/\s+/g, '')
	if (digits.startsWith('+421') && digits.length === 13) {
		return `+421 ${digits.slice(4, 7)} ${digits.slice(7, 10)} ${digits.slice(10)}`
	}
	if (digits.startsWith('0') && digits.length === 10) {
		return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`
	}
	return phone
}

type JobPair = { address: string; cleanerName: string }

type Props = {
	title: string
	jobPairs?: JobPair[]
	contactName?: string | null
	contactPhone?: string | null
	contactEmail?: string | null
	organizationName?: string | null
	callStatus: TELEPHONE_SURVEY_CALL_STATUS | null
	satisfaction: TELEPHONE_SURVEY_SATISFACTION | null
	note: string
	scheduledCallAt?: string | null
	isSaving?: boolean
	onCancel?: () => void
	onEditSurvey?: () => void
	onCallStatusChange: (status: TELEPHONE_SURVEY_CALL_STATUS) => void
	onSatisfactionChange: (status: TELEPHONE_SURVEY_SATISFACTION | null) => void
	onNoteChange: (note: string) => void
	onScheduledCallAtChange: (date: string | null) => void
	onSubmit: (values?: {
		callStatus?: TELEPHONE_SURVEY_CALL_STATUS
		satisfaction?: TELEPHONE_SURVEY_SATISFACTION | null
		note?: string
		scheduledCallAt?: string | null
	}) => void
	trusteeUserID?: number | null
	trusteeFirstName?: string | null
	trusteeLastName?: string | null
	trusteeEmail?: string | null
	onTrusteeUpdated?: (field: 'firstName' | 'lastName' | 'email', value: string) => void
}

const FIELD_LABELS: Record<'firstName' | 'lastName' | 'email', string> = {
	firstName: 'Meno',
	lastName: 'Priezvisko',
	email: 'Email'
}

const TelephoneSurveyCallCard = ({
	title,
	jobPairs,
	contactName,
	contactPhone,
	organizationName,
	callStatus,
	satisfaction,
	note,
	isSaving,
	onCancel,
	onEditSurvey,
	onCallStatusChange,
	onSatisfactionChange,
	onNoteChange,
	onSubmit,
	trusteeUserID,
	trusteeFirstName,
	trusteeLastName,
	trusteeEmail,
	onTrusteeUpdated
}: Props) => {
	const [editingField, setEditingField] = useState<'firstName' | 'lastName' | 'email' | null>(null)
	const [editingValue, setEditingValue] = useState<string>('')
	const [localValues, setLocalValues] = useState(() => ({
		firstName: trusteeFirstName ?? '',
		lastName: trusteeLastName ?? '',
		email: trusteeEmail ?? ''
	}))
	const [inlineInputValues, setInlineInputValues] = useState(() => ({
		firstName: '',
		lastName: '',
		email: ''
	}))
	const [savingField, setSavingField] = useState<'firstName' | 'lastName' | 'email' | null>(null)

	const saveTrusteeField = async (field: 'firstName' | 'lastName' | 'email', value: string) => {
		if (!trusteeUserID) {
			message.error('Chýba ID domovníka')
			return
		}
		setSavingField(field)
		try {
			await patchReq(`/api/admin/users/${trusteeUserID}`, null, { [field]: value })
			message.success('Údaje uložené')
			setLocalValues((prev) => ({ ...prev, [field]: value }))
			setInlineInputValues((prev) => ({ ...prev, [field]: '' }))
			setEditingField(null)
			onTrusteeUpdated?.(field, value)
		} catch {
			message.error('Nepodarilo sa uložiť údaje')
		} finally {
			setSavingField(null)
		}
	}

	let step: 'call' | 'rating' | 'comment' = 'call'
	if (callStatus && !satisfaction) {
		step = 'rating'
	}
	if (satisfaction) {
		step = 'comment'
	}

	let stepIndex = 1
	if (step === 'rating') {
		stepIndex = 2
	}
	if (step === 'comment') {
		stepIndex = 3
	}

	const stepCircle = (index: number) => {
		const isDone = stepIndex > index
		const isActive = stepIndex === index
		let background = '#d1d5db'
		if (isActive) {
			background = '#337ab7'
		}
		if (isDone) {
			background = '#22c55e'
		}
		return (
			<div style={{
				width: 34,
				height: 34,
				borderRadius: '50%',
				background,
				color: isActive || isDone ? '#fff' : '#4b5563',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				fontSize: 17,
				fontWeight: 700
			}}
			>
				{index}
			</div>
		)
	}

	const connector = <div style={{ width: 64, height: 4, background: '#d1d5db' }} />

	const handleAnswered = () => {
		onCallStatusChange(TELEPHONE_SURVEY_CALL_STATUS.ANSWERED)
	}

	const handleNoAnswer = () => {
		onSubmit({
			callStatus: TELEPHONE_SURVEY_CALL_STATUS.NO_ANSWER,
			satisfaction: null,
			note: '',
			scheduledCallAt: null
		})
	}

	const handleRating = (value: TELEPHONE_SURVEY_SATISFACTION) => {
		onSatisfactionChange(value)
	}

	const handleSave = () => {
		onSubmit({
			callStatus: TELEPHONE_SURVEY_CALL_STATUS.ANSWERED,
			satisfaction,
			note,
			scheduledCallAt: null
		})
	}

	return (
		<Card
			title={(
				<span style={{
					display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 700
				}}>
					{title}
					{onEditSurvey && (
						<EditOutlined
							onClick={(e) => { e.stopPropagation(); onEditSurvey() }}
							style={{
								fontSize: 15, color: '#6b7280', cursor: 'pointer', fontWeight: 400
							}}
							title={'Upraviť posledný prieskum'}
						/>
					)}
				</span>
			)}
			bodyStyle={{ padding: 28 }}
			style={{
				width: 'min(900px, 100%)',
				margin: '0 auto',
				borderRadius: 8,
				boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)'
			}}
		>
			<div
				style={{
					background: '#eff6ff',
					border: '1px solid #bfdbfe',
					borderRadius: 8,
					padding: '10px 14px',
					marginBottom: 16
				}}
			>
				{/* Single-row header: name · phone · organization */}
				<div style={{
					display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 16px'
				}}>
					<span style={{ fontWeight: 800, color: '#1f2937', fontSize: 15 }}>{contactName || '-'}</span>
					{contactPhone && (
						<span style={{ fontWeight: 700, color: '#337ab7', fontSize: 13 }}>{formatPhone(contactPhone)}</span>
					)}
					{organizationName && (
						<span style={{ color: '#6b7280', fontSize: 12, marginLeft: 'auto' }}>
							<span style={{ fontWeight: 600 }}>Organizácia: </span>{organizationName}
						</span>
					)}
				</div>
				{/* Job pairs: address — Upratuje: cleanerName */}
				{jobPairs?.length ? (
					<div style={{
						fontSize: 12, color: '#6b7280', lineHeight: '20px', borderTop: '1px solid #dbeafe', paddingTop: 6, marginTop: 8
					}}>
						<ul style={{ margin: 0, paddingLeft: 18 }}>
							{jobPairs.map((pair) => (
								<li key={pair.address}>
									<span style={{ color: '#374151' }}>{pair.address}</span>
									{pair.cleanerName && (
										<span> — <span style={{ fontWeight: 600 }}>Upratuje:</span> {pair.cleanerName}</span>
									)}
								</li>
							))}
						</ul>
					</div>
				) : null}
			</div>

			{trusteeUserID && (
				<div style={{
					background: '#f9fafb',
					border: '1px solid #e5e7eb',
					borderRadius: 8,
					padding: '12px 14px',
					marginBottom: 16
				}}>
					<div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 10 }}>Kontaktné údaje domovníka</div>
					{([
						{ field: 'firstName' as const, label: 'Meno', placeholder: 'Zadajte meno' },
						{ field: 'lastName' as const, label: 'Priezvisko', placeholder: 'Zadajte priezvisko' },
						{ field: 'email' as const, label: 'Email', placeholder: 'Zadajte email' }
					]).map(({ field, label, placeholder }) => {
						const currentValue = localValues[field]
						return (
							<div key={field} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
								<div style={{ width: 80, color: '#6b7280', fontSize: 13 }}>{label}:</div>
								<div style={{ flex: 1 }}>
									{!currentValue ? (
										<Input
											value={inlineInputValues[field]}
											onChange={(e) => setInlineInputValues((prev) => ({ ...prev, [field]: e.target.value }))}
											placeholder={placeholder}
											style={{ background: '#fffbeb', borderColor: '#fcd34d' }}
										/>
									) : (
										<span style={{ fontSize: 14, color: '#1f2937' }}>{currentValue}</span>
									)}
								</div>
								{!currentValue ? (
									<Button
										size={'small'}
										type={'primary'}
										loading={savingField === field}
										onClick={() => saveTrusteeField(field, inlineInputValues[field])}
										disabled={!inlineInputValues[field]?.trim()}
									>
										Uložiť
									</Button>
								) : (
									<Button
										size={'small'}
										onClick={() => { setEditingField(field); setEditingValue(currentValue) }}
									>
										Upraviť
									</Button>
								)}
							</div>
						)
					})}
				</div>
			)}

			<div style={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				marginBottom: 30
			}}
			>
				{stepCircle(1)}
				{connector}
				{stepCircle(2)}
				{connector}
				{stepCircle(3)}
			</div>

			{step === 'call' && (
				<div>
					<h3 style={{
						color: '#1f2937', fontSize: 21, fontWeight: 800, marginBottom: 18
					}}>
						Krok 1: Zavolajte zákazníkovi
					</h3>
					<p style={{ color: '#4b5563', fontSize: 15, marginBottom: 18 }}>
						Zavolajte na číslo: <strong>{formatPhone(contactPhone)}</strong>
					</p>
					<Button
						block
						type={'primary'}
						onClick={handleAnswered}
						style={{
							height: 54,
							background: '#22c55e',
							borderColor: '#22c55e',
							borderRadius: 8,
							fontSize: 17,
							fontWeight: 800,
							marginBottom: 14
						}}
					>
						✓ Zodvihol
					</Button>
					<Button
						block
						type={'primary'}
						onClick={handleNoAnswer}
						loading={isSaving}
						style={{
							height: 54,
							background: '#f97316',
							borderColor: '#f97316',
							borderRadius: 8,
							fontSize: 17,
							fontWeight: 800
						}}
					>
						× Nezodvihol
					</Button>
				</div>
			)}

			{step === 'rating' && (
				<div>
					<h3 style={{
						color: '#1f2937', fontSize: 21, fontWeight: 800, marginBottom: 18
					}}>
						Krok 2: Hodnotenie spokojnosti
					</h3>
					<p style={{ color: '#4b5563', fontSize: 15, marginBottom: 18 }}>
						Opýtajte sa: &quot;Ste spokojný/á s upratovaním?&quot;
					</p>
					<Button
						block
						onClick={() => handleRating(TELEPHONE_SURVEY_SATISFACTION.SATISFIED)}
						style={{
							height: 54,
							background: '#facc15',
							borderColor: '#facc15',
							color: '#1f2937',
							borderRadius: 8,
							fontSize: 17,
							fontWeight: 800,
							marginBottom: 14
						}}
					>
						☀ Spokojný
					</Button>
					<Button
						block
						type={'primary'}
						onClick={() => handleRating(TELEPHONE_SURVEY_SATISFACTION.UNSATISFIED)}
						style={{
							height: 54,
							background: '#ef4444',
							borderColor: '#ef4444',
							borderRadius: 8,
							fontSize: 17,
							fontWeight: 800
						}}
					>
						☀ Nespokojný
					</Button>
				</div>
			)}

			{step === 'comment' && (
				<div>
					<h3 style={{
						color: '#1f2937', fontSize: 21, fontWeight: 800, marginBottom: 18
					}}>
						Krok 3: Poznámka
					</h3>
					<p style={{ color: '#4b5563', fontSize: 15, marginBottom: 18 }}>
						Hodnotenie:
						<span style={{
							marginLeft: 8,
							color: satisfaction === TELEPHONE_SURVEY_SATISFACTION.SATISFIED ? '#a16207' : '#dc2626',
							fontWeight: 800
						}}
						>
							{satisfaction === TELEPHONE_SURVEY_SATISFACTION.SATISFIED ? '☀ Spokojný' : '☀ Nespokojný'}
						</span>
					</p>
					<Input.TextArea
						value={note}
						rows={6}
						placeholder={'Napíšte poznámku k hovoru (napr. pochvala, reklamácia, detaily...)'}
						onChange={(e) => onNoteChange(e.target.value)}
						style={{ marginBottom: 18 }}
					/>
					<div style={{ display: 'flex', gap: 12 }}>
						<Button
							block
							onClick={() => onSatisfactionChange(null)}
							style={{
								height: 44,
								borderRadius: 8,
								background: '#d1d5db',
								borderColor: '#d1d5db',
								color: '#1f2937',
								fontWeight: 700
							}}
						>
							← Späť
						</Button>
						<Button
							block
							type={'primary'}
							onClick={handleSave}
							loading={isSaving}
							style={{
								height: 44,
								borderRadius: 8,
								background: '#337ab7',
								borderColor: '#337ab7',
								fontWeight: 700
							}}
						>
							Uložiť prieskum
						</Button>
					</div>
				</div>
			)}

			{onCancel && (
				<Button
					type={'link'}
					block
					onClick={onCancel}
					style={{ marginTop: 16, color: '#6b7280', fontWeight: 700 }}
				>
					Zrušiť
				</Button>
			)}

			<Modal
				visible={editingField !== null}
				title={editingField ? `Upraviť ${FIELD_LABELS[editingField]}` : ''}
				onCancel={() => setEditingField(null)}
				onOk={() => editingField && saveTrusteeField(editingField, editingValue)}
				okText={'Uložiť'}
				cancelText={'Zrušiť'}
				confirmLoading={savingField !== null}
				destroyOnClose
			>
				{editingField && (
					<>
						<div style={{ marginBottom: 8, fontSize: 13, color: '#6b7280' }}>Pôvodná hodnota:</div>
						<div style={{
							background: '#f3f4f6', padding: '8px 12px', borderRadius: 6, marginBottom: 14, color: '#1f2937', fontSize: 14
						}}>
							{editingField ? (localValues[editingField] || '-') : '-'}
						</div>
						<div style={{ marginBottom: 8, fontSize: 13, color: '#6b7280' }}>Nová hodnota:</div>
						<Input value={editingValue} onChange={(e) => setEditingValue(e.target.value)} autoFocus />
					</>
				)}
			</Modal>
		</Card>
	)
}

export default TelephoneSurveyCallCard
